import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { LiveStreamLink, RecentMedia } from 'src/models/live-stream-link';
import { NextGenTvAsset } from 'src/models/nextgen-tv-asset.model';
import { BehaviorSubject } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { AlertController, ToastController } from '@ionic/angular';
import { Camera, CameraResultType, CameraSource, ImageOptions } from '@capacitor/camera';
import { ImageService } from './image.service';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { OrganizationRequiredDocument } from 'src/models/organization-required-document';
import { RegistrationChangeRequest } from 'src/models/registration-change-request';
import { AlertMessager } from 'src/libs/alertMessager';

@Injectable({
  providedIn: 'root',
})
export class MediaService {
  public selectedNextGenTvAsset = new BehaviorSubject<
    NextGenTvAsset | undefined
    >(undefined);
  
  btns = [
    {
      text: 'Take a picture',
      role: 'camera'
    },
    {
      text: 'Upload a photo',
      role: 'image'
    },
    {
      text: 'Upload a file',
      role: 'file',
    },
    {
      text: 'Cancel',
      role: 'cancel'
    },
  ];

  constructor(
    private api: ApiService,
    private alert: AlertController,
    private alertMessager: AlertMessager,
    private imageService: ImageService
  ) {}

  async showUploadOptions(fileName: string, accountId: number) {
    const selectedBtn = await this.presentUploadOptions();
    console.log(selectedBtn);

    if (!selectedBtn.role || selectedBtn.role === 'cancel' || selectedBtn.role === 'backdrop') {
        return false;
    } 

    try {
      if (selectedBtn.role === 'file') {
        const success = await this.handlePdfUpload(fileName, accountId);
        if (!success) {
            return false;
        }
      } else {
        await this.handleImageUpload(fileName, accountId, selectedBtn.role === 'camera');
      }
      this.alertMessager.showToast(undefined, 'Document uploaded successfully!', 'success');
    } catch (err) {
      this.alertMessager.showToast(undefined, 'Could not upload document. Please try again later.', 'danger');
    }

    return true;
  }

  async presentUploadOptions(): Promise<{ text: string; role: string } | undefined> {
    const alertOrActionSheet = await this.alert.create({
      cssClass: 'file-upload',
      buttons: this.btns,
      header: 'Upload options'
    });
  
    await alertOrActionSheet.present();
  
    const result = await alertOrActionSheet.onDidDismiss();
  
    const selectedBtn = this.btns.find(btn => btn.role === result.role);

    return selectedBtn;
  }

  async getStreamingLinks(rodeoId: number) {
    return await this.api.get<LiveStreamLink[]>(
      `${this.api.endpoints.streaming}/GetLiveStreamLinks?rodeoId=${rodeoId}`,
    );
  }

  async getRodeoEventStreamingLinks(rodeoEventId: number, rodeoId: number) {
    return await this.api.get<LiveStreamLink[]>(
      `${this.api.endpoints.streaming}/GetLiveStreamLinks?rodeoEventId=${rodeoEventId}&rodeoId=${rodeoId}`,
    );
  }

  async getOrganizationMediaContent(organizationId: number) {
    return this.api.get<RecentMedia>(
      `${this.api.endpoints.streaming}/GetOrganizationMediaContent?orgId=${organizationId}`,
    );
  }

  async saveLiveStreamLink(link: LiveStreamLink, startStreaming: boolean) {
    return this.api.post<LiveStreamLink>(
      `${this.api.endpoints.streaming}/SaveLiveStreamLink?startStreaming=${startStreaming ? 'true' : 'false'}`,
      link,
    );
  }

  async saveStreamingAsset(asset: NextGenTvAsset, video?: any, image?: any) {
    let formData: FormData = new FormData();

    formData.append('request', JSON.stringify(asset));
    if (video) {
      formData.append('video', video, video.name);
    }
    if (image) {
      formData.append('image', image, image.name);
    }

    return this.api.postAndReportProgress<NextGenTvAsset>(
      `${this.api.endpoints.streaming}/SaveStreamingAsset`,
      formData,
    );
  }

  async stopLiveEvent(linkId: string) {
    await this.api.post(
      `${this.api.endpoints.streaming}/StopLiveEvent?linkId=${linkId}`,
      undefined,
    );
  }

  async getStreamingAsset(assetId: string) {
    return this.api.get<NextGenTvAsset>(
      `${this.api.endpoints.streaming}/GetStreamingAsset?id=${assetId}`,
    );
  }

  async deleteStreamingAsset(assetId: string) {
    await this.api.delete<NextGenTvAsset>(
      `${this.api.endpoints.streaming}/DeleteStreamingAsset?id=${assetId}`,
    );
    this.selectedNextGenTvAsset.next(undefined);
  }

  async handleImageUpload(fileName: string, accountId: number, useCamera: boolean) {
    const options: ImageOptions = {
        quality: 100,
        allowEditing: true,
        resultType: CameraResultType.Base64,
        source: useCamera ? CameraSource.Camera : CameraSource.Photos,
        width: 1024,
        height: 1024,
    };
    
    let cameraPermissions = await Camera.checkPermissions();
    console.log(cameraPermissions);
    
    if (Capacitor.isNativePlatform()) {
        const permission = useCamera ? cameraPermissions.camera : cameraPermissions.photos;
        if (permission === 'denied') {
          this.alertMessager.showToast(undefined, 'NextGen does not have access to your ' + useCamera ? 'camera.' : 'photos.'
            + ' You will need to enable this on your device settings.', 'danger')
          return undefined;
        }
    }

    const image = await Camera.getPhoto(options);

    console.log(image);

    const blob = this.binaryStringToBlob(image.base64String as string, image.format);

    await this.imageService.uploadImage(blob, fileName, true, accountId);

    if (!fileName.endsWith(`.${image.format}`)) {
      fileName += `.${image.format}`;
    }

    return fileName;
  }

  async handlePdfUpload(fileName: string, accountId: number) {
    let result: any;
    try {
        result = await FilePicker.pickFiles({
            types: ['application/pdf'],
            multiple: false,
            readData: true
          });
    } catch (err) {
        console.error(err);
        //swallow this. it fails when people cancel
    }

    console.log(result.files);
    
    if (result.files.length > 0) {
      const file = result.files[0];
      let blob = file.blob;
      
      if (Capacitor.isNativePlatform()) {
          const base64 = file.data;
          blob = this.binaryStringToBlob(base64, file.mimeType);
      }
      
      console.log(blob);
      
      await this.imageService.uploadImage(
        blob, 
        fileName, 
        true, 
        accountId);
      
      if (!fileName.endsWith('.pdf')) {
        fileName += '.pdf';
      }
    }
    return fileName;
  }

  generateFileName(orgName?: string, docName?: string) {
    // Example: accountId/documents/orgName/docName.fileType
    let fileName = '';

    if (orgName) {
      const sanitizedName = orgName
      .replace(/[^a-z0-9]/gi, '')
        .replace(/\s+/g, '');
      
      fileName = sanitizedName;
    }

    if (docName) {
        const sanitizedName = docName
        .replace(/[^a-z0-9]/gi, '') // Remove non-alphanumeric characters
        .replace(/\s+/g, '');      // Remove any whitespace
      
      if (orgName) {
        fileName += `/${sanitizedName}`;
      } else {
        fileName = sanitizedName;
      }
    }

    console.log('generated fileName', fileName);
    return fileName;
  } 

  formatUrl(fileType: string, fileName: string) {
    const hostUrl = 'https://nextgenrodeomedia.blob.core.windows.net';

    switch (fileType) {
      case 'image':
        return `${hostUrl}/images/${fileName}`;
      case 'file':
        return `${hostUrl}/files/${fileName}`;
      default:
        return fileName;
    }
  }

  private binaryStringToBlob(base64Data: string, type: string) {
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
        const begin = sliceIndex * sliceSize;
        const end = Math.min(begin + sliceSize, bytesLength);

        const bytes = new Array(end - begin);
        for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
            bytes[i] = byteCharacters[offset].charCodeAt(0);
        }
        byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type });
  }

  downloadDynamicCSV(data: any[], fileName: string) {
    if (!data || data.length === 0) {
        console.error("No data available to export.");
        return;
    }

    let csvContent = "";
    let previousHeaders: string[] = [];

    data.forEach((item, index) => {
        // Extract current object keys
        const currentHeaders = Object.keys(item);

        // If headers differ from the previous object, write new header row
        if (index === 0 || !this.arraysEqual(previousHeaders, currentHeaders)) {
            csvContent += currentHeaders.join(",") + "\n"; // Add header row
            previousHeaders = currentHeaders; // Update previous headers
        }

        // Add the corresponding values row
        const values = currentHeaders.map(key => {
            let value = item[key];
            return typeof value === "string" ? `"${value.replace(/"/g, '""')}"` : value; // Escape quotes
        });

        csvContent += values.join(",") + "\n";
    });

    // Convert content to Blob
    const blob = new Blob([csvContent], { type: "text/csv" });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName.endsWith(".csv") ? fileName : `${fileName}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  // Helper function to check if two arrays are equal
  arraysEqual(arr1: string[], arr2: string[]): boolean {
    return arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index]);
  }
}
